import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { 
  Star, 
  Search, 
  Download, 
  RefreshCw, 
  Filter,
  ChevronUp,
  ChevronDown,
  Calendar,
  Building2,
  User,
  FileText,
  Clock,
  X,
  BarChart3
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { parseLocalDate, formatLocalDate } from '../utils/dateHelpers';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import './ConsultaCalificaciones.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ConsultaCalificaciones = ({ onViewChange }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [gestiones, setGestiones] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 'updatedAt', direction: 'desc' });
  
  // Filtros
  const [filtros, setFiltros] = useState({
    nomenclatura: '',
    institucion: '',
    asesorProbidad: '',
    asesorCNC: '',
    tipoGestion: '',
    calificacionMin: '',
    calificacionMax: '',
    fechaDesde: '',
    fechaHasta: ''
  });
  
  const [showFilters, setShowFilters] = useState(true);

  useEffect(() => {
    fetchGestiones();
  }, []);

  const fetchGestiones = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/gestiones?limit=2000`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = response.data.gestiones || response.data || [];
      // Filtrar solo gestiones con calificación
      const conCalificacion = data.filter(g => 
        g.calificacion_instancia?.puntuacion || g.estado === 'Completada / Archivada'
      );
      setGestiones(conCalificacion);
    } catch (error) {
      console.error('Error al cargar gestiones:', error);
      toast.error('Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  };

  // Calcular tiempo de atención (días)
  const calcularTiempoAtencion = (fechaAsignacion, fechaCompletado) => {
    if (!fechaAsignacion) return null;
    const inicio = parseLocalDate(fechaAsignacion);
    const fin = fechaCompletado ? parseLocalDate(fechaCompletado) : new Date();
    if (!inicio || !fin) return null;
    const diffMs = fin - inicio;
    const dias = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    return dias;
  };

  // Obtener valores únicos para los filtros
  const valoresUnicos = useMemo(() => {
    return {
      instituciones: [...new Set(gestiones.map(g => g.institucion).filter(Boolean))].sort(),
      asesoresProbidad: [...new Set(gestiones.map(g => g.asesor_probidad_nombre).filter(Boolean))].sort(),
      asesoresCNC: [...new Set(gestiones.map(g => g.nombre_asesor_cnc).filter(Boolean))].sort(),
      tiposGestion: [...new Set(gestiones.map(g => g.tipo_gestion).filter(Boolean))].sort()
    };
  }, [gestiones]);

  // Filtrar y ordenar datos
  const datosFiltrados = useMemo(() => {
    let resultado = [...gestiones];
    
    // Aplicar filtros
    if (filtros.nomenclatura) {
      resultado = resultado.filter(g => 
        g.nomenclatura?.toLowerCase().includes(filtros.nomenclatura.toLowerCase())
      );
    }
    if (filtros.institucion) {
      resultado = resultado.filter(g => g.institucion === filtros.institucion);
    }
    if (filtros.asesorProbidad) {
      resultado = resultado.filter(g => g.asesor_probidad_nombre === filtros.asesorProbidad);
    }
    if (filtros.asesorCNC) {
      resultado = resultado.filter(g => g.nombre_asesor_cnc === filtros.asesorCNC);
    }
    if (filtros.tipoGestion) {
      resultado = resultado.filter(g => g.tipo_gestion === filtros.tipoGestion);
    }
    if (filtros.calificacionMin) {
      resultado = resultado.filter(g => 
        (g.calificacion_instancia?.puntuacion || 0) >= parseInt(filtros.calificacionMin)
      );
    }
    if (filtros.calificacionMax) {
      resultado = resultado.filter(g => 
        (g.calificacion_instancia?.puntuacion || 0) <= parseInt(filtros.calificacionMax)
      );
    }
    if (filtros.fechaDesde) {
      const fechaDesde = parseLocalDate(filtros.fechaDesde);
      resultado = resultado.filter(g => {
        const fechaG = g.updatedAt ? parseLocalDate(g.updatedAt) : null;
        return fechaG && fechaG >= fechaDesde;
      });
    }
    if (filtros.fechaHasta) {
      const fechaHasta = parseLocalDate(filtros.fechaHasta);
      resultado = resultado.filter(g => {
        const fechaG = g.updatedAt ? parseLocalDate(g.updatedAt) : null;
        return fechaG && fechaG <= fechaHasta;
      });
    }
    
    // Ordenar
    if (sortConfig.key) {
      resultado.sort((a, b) => {
        let aVal, bVal;
        
        switch (sortConfig.key) {
          case 'calificacion':
            aVal = a.calificacion_instancia?.puntuacion || 0;
            bVal = b.calificacion_instancia?.puntuacion || 0;
            break;
          case 'tiempoAtencion':
            aVal = calcularTiempoAtencion(a.fecha_asignacion, a.updatedAt) || 999;
            bVal = calcularTiempoAtencion(b.fecha_asignacion, b.updatedAt) || 999;
            break;
          case 'fechaAsignacion':
            aVal = a.fecha_asignacion ? new Date(a.fecha_asignacion) : new Date(0);
            bVal = b.fecha_asignacion ? new Date(b.fecha_asignacion) : new Date(0);
            break;
          case 'updatedAt':
            aVal = a.updatedAt ? new Date(a.updatedAt) : new Date(0);
            bVal = b.updatedAt ? new Date(b.updatedAt) : new Date(0);
            break;
          default:
            aVal = a[sortConfig.key] || '';
            bVal = b[sortConfig.key] || '';
        }
        
        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    
    return resultado;
  }, [gestiones, filtros, sortConfig]);

  // Manejar ordenamiento
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Renderizar icono de ordenamiento
  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />;
  };

  // Renderizar estrellas
  const renderEstrellas = (puntuacion) => {
    if (!puntuacion) return <span style={{ color: '#999' }}>Sin calificar</span>;
    return (
      <span style={{ color: '#FFB800', fontSize: '1rem' }}>
        {'★'.repeat(puntuacion)}{'☆'.repeat(5 - puntuacion)}
        <span style={{ marginLeft: '0.5rem', color: '#666', fontSize: '0.85rem' }}>({puntuacion}/5)</span>
      </span>
    );
  };

  // Limpiar filtros
  const limpiarFiltros = () => {
    setFiltros({
      nomenclatura: '',
      institucion: '',
      asesorProbidad: '',
      asesorCNC: '',
      tipoGestion: '',
      calificacionMin: '',
      calificacionMax: '',
      fechaDesde: '',
      fechaHasta: ''
    });
  };

  // Exportar a Excel
  const handleExportarExcel = () => {
    const datosExport = datosFiltrados.map(g => ({
      'Código': g.nomenclatura,
      'Institución': g.institucion,
      'Asesor Probidad': g.asesor_probidad_nombre || '-',
      'Asesor CNC': g.nombre_asesor_cnc || '-',
      'Tipo Gestión': g.tipo_gestion,
      'Calificación': g.calificacion_instancia?.puntuacion || '-',
      'Observaciones': g.calificacion_instancia?.comentario || '-',
      'Tiempo Atención (días)': calcularTiempoAtencion(g.fecha_asignacion, g.updatedAt) || '-',
      'Fecha Asignación': g.fecha_asignacion ? formatLocalDate(g.fecha_asignacion) : '-',
      'Fecha Completado': g.updatedAt && g.estado === 'Completada / Archivada' ? formatLocalDate(g.updatedAt) : '-',
      'Estado': g.estado
    }));
    
    const ws = XLSX.utils.json_to_sheet(datosExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Calificaciones');
    XLSX.writeFile(wb, `Calificaciones_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success('Excel exportado correctamente');
  };

  // Exportar a PDF
  const handleExportarPDF = () => {
    const doc = new jsPDF('landscape');
    
    doc.setFontSize(16);
    doc.text('Consulta de Calificaciones por Gestión', 14, 15);
    doc.setFontSize(10);
    doc.text(`Generado: ${new Date().toLocaleString('es-ES')}`, 14, 22);
    
    const tableData = datosFiltrados.map(g => [
      g.nomenclatura,
      (g.institucion || '').substring(0, 25),
      (g.asesor_probidad_nombre || '-').substring(0, 20),
      (g.nombre_asesor_cnc || '-').substring(0, 20),
      g.calificacion_instancia?.puntuacion ? `${g.calificacion_instancia.puntuacion}/5` : '-',
      calcularTiempoAtencion(g.fecha_asignacion, g.updatedAt) || '-',
      g.fecha_asignacion ? formatLocalDate(g.fecha_asignacion) : '-'
    ]);
    
    doc.autoTable({
      head: [['Código', 'Institución', 'Asesor Prob.', 'Asesor CNC', 'Calif.', 'Días', 'F. Asign.']],
      body: tableData,
      startY: 28,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [102, 126, 234] }
    });
    
    doc.save(`Calificaciones_${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success('PDF exportado correctamente');
  };

  if (loading) {
    return (
      <div className="consulta-loading">
        <div className="loader"></div>
        <p>Cargando calificaciones...</p>
      </div>
    );
  }

  return (
    <div className="consulta-calificaciones-container" data-testid="consulta-calificaciones">
      <div className="consulta-header">
        <div>
          <h1>
            <Star size={28} />
            Consulta de Calificaciones
          </h1>
          <p>Visualización de calificaciones asociadas a cada gestión</p>
        </div>
        <div className="consulta-actions">
          {onViewChange && (
            <button 
              className="btn btn-primary" 
              onClick={() => onViewChange('tablero-calificaciones')}
              data-testid="btn-metricas-evaluacion"
              style={{ background: '#1565C0' }}
            >
              <BarChart3 size={16} />
              Métricas de Evaluación
            </button>
          )}
          <button className="btn btn-secondary" onClick={fetchGestiones} disabled={loading}>
            <RefreshCw size={16} className={loading ? 'spin' : ''} />
            Actualizar
          </button>
          <button className="btn btn-secondary" onClick={handleExportarExcel}>
            <Download size={16} />
            Excel
          </button>
          <button className="btn btn-primary" onClick={handleExportarPDF}>
            <Download size={16} />
            PDF
          </button>
        </div>
      </div>

      {/* Panel de Filtros */}
      <div className="filtros-panel">
        <div className="filtros-header" onClick={() => setShowFilters(!showFilters)}>
          <h3>
            <Filter size={18} />
            Filtros de Búsqueda
          </h3>
          <button className="btn-toggle">
            {showFilters ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </button>
        </div>
        
        {showFilters && (
          <div className="filtros-content">
            <div className="filtros-grid">
              <div className="filtro-item">
                <label><FileText size={14} /> Código Gestión</label>
                <input
                  type="text"
                  placeholder="Buscar por código..."
                  value={filtros.nomenclatura}
                  onChange={(e) => setFiltros({...filtros, nomenclatura: e.target.value})}
                />
              </div>
              
              <div className="filtro-item">
                <label><Building2 size={14} /> Institución</label>
                <select
                  value={filtros.institucion}
                  onChange={(e) => setFiltros({...filtros, institucion: e.target.value})}
                >
                  <option value="">Todas</option>
                  {valoresUnicos.instituciones.map(inst => (
                    <option key={inst} value={inst}>{inst}</option>
                  ))}
                </select>
              </div>
              
              <div className="filtro-item">
                <label><User size={14} /> Asesor Probidad</label>
                <select
                  value={filtros.asesorProbidad}
                  onChange={(e) => setFiltros({...filtros, asesorProbidad: e.target.value})}
                >
                  <option value="">Todos</option>
                  {valoresUnicos.asesoresProbidad.map(asesor => (
                    <option key={asesor} value={asesor}>{asesor}</option>
                  ))}
                </select>
              </div>
              
              <div className="filtro-item">
                <label><User size={14} /> Asesor CNC</label>
                <select
                  value={filtros.asesorCNC}
                  onChange={(e) => setFiltros({...filtros, asesorCNC: e.target.value})}
                >
                  <option value="">Todos</option>
                  {valoresUnicos.asesoresCNC.map(asesor => (
                    <option key={asesor} value={asesor}>{asesor}</option>
                  ))}
                </select>
              </div>
              
              <div className="filtro-item">
                <label><FileText size={14} /> Tipo Gestión</label>
                <select
                  value={filtros.tipoGestion}
                  onChange={(e) => setFiltros({...filtros, tipoGestion: e.target.value})}
                >
                  <option value="">Todos</option>
                  {valoresUnicos.tiposGestion.map(tipo => (
                    <option key={tipo} value={tipo}>{tipo}</option>
                  ))}
                </select>
              </div>
              
              <div className="filtro-item">
                <label><Star size={14} /> Calificación (min-max)</label>
                <div className="filtro-rango">
                  <select
                    value={filtros.calificacionMin}
                    onChange={(e) => setFiltros({...filtros, calificacionMin: e.target.value})}
                  >
                    <option value="">Min</option>
                    {[1,2,3,4,5].map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                  <span>-</span>
                  <select
                    value={filtros.calificacionMax}
                    onChange={(e) => setFiltros({...filtros, calificacionMax: e.target.value})}
                  >
                    <option value="">Max</option>
                    {[1,2,3,4,5].map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
              </div>
              
              <div className="filtro-item">
                <label><Calendar size={14} /> Fecha Desde</label>
                <input
                  type="date"
                  value={filtros.fechaDesde}
                  onChange={(e) => setFiltros({...filtros, fechaDesde: e.target.value})}
                />
              </div>
              
              <div className="filtro-item">
                <label><Calendar size={14} /> Fecha Hasta</label>
                <input
                  type="date"
                  value={filtros.fechaHasta}
                  onChange={(e) => setFiltros({...filtros, fechaHasta: e.target.value})}
                />
              </div>
            </div>
            
            <div className="filtros-actions">
              <button className="btn btn-secondary" onClick={limpiarFiltros}>
                <X size={16} />
                Limpiar Filtros
              </button>
              <span className="resultados-count">
                {datosFiltrados.length} de {gestiones.length} registros
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Tabla de Resultados */}
      <div className="tabla-container">
        <div className="tabla-wrapper">
          <table className="tabla-calificaciones" data-testid="tabla-calificaciones">
            <thead>
              <tr>
                <th onClick={() => handleSort('nomenclatura')} className="sortable">
                  Código {renderSortIcon('nomenclatura')}
                </th>
                <th onClick={() => handleSort('institucion')} className="sortable">
                  Institución {renderSortIcon('institucion')}
                </th>
                <th onClick={() => handleSort('asesor_probidad_nombre')} className="sortable">
                  Asesor Probidad {renderSortIcon('asesor_probidad_nombre')}
                </th>
                <th onClick={() => handleSort('nombre_asesor_cnc')} className="sortable">
                  Asesor CNC {renderSortIcon('nombre_asesor_cnc')}
                </th>
                <th onClick={() => handleSort('tipo_gestion')} className="sortable">
                  Tipo Gestión {renderSortIcon('tipo_gestion')}
                </th>
                <th onClick={() => handleSort('calificacion')} className="sortable">
                  Calificación {renderSortIcon('calificacion')}
                </th>
                <th>Observaciones</th>
                <th onClick={() => handleSort('tiempoAtencion')} className="sortable">
                  Tiempo Atención {renderSortIcon('tiempoAtencion')}
                </th>
                <th onClick={() => handleSort('fechaAsignacion')} className="sortable">
                  F. Asignación {renderSortIcon('fechaAsignacion')}
                </th>
                <th onClick={() => handleSort('updatedAt')} className="sortable">
                  F. Completado {renderSortIcon('updatedAt')}
                </th>
              </tr>
            </thead>
            <tbody>
              {datosFiltrados.length === 0 ? (
                <tr>
                  <td colSpan="10" className="empty-row">
                    <Star size={48} style={{ opacity: 0.3 }} />
                    <p>No se encontraron registros con los filtros aplicados</p>
                  </td>
                </tr>
              ) : (
                datosFiltrados.map((g, index) => {
                  const tiempoAtencion = calcularTiempoAtencion(g.fecha_asignacion, g.updatedAt);
                  return (
                    <tr key={g.id || index}>
                      <td className="codigo-cell">
                        <strong>{g.nomenclatura}</strong>
                      </td>
                      <td className="institucion-cell" title={g.institucion}>
                        {g.institucion || '-'}
                      </td>
                      <td>{g.asesor_probidad_nombre || '-'}</td>
                      <td>{g.nombre_asesor_cnc || '-'}</td>
                      <td className="tipo-cell">{g.tipo_gestion}</td>
                      <td className="calificacion-cell">
                        {renderEstrellas(g.calificacion_instancia?.puntuacion)}
                      </td>
                      <td className="observaciones-cell" title={g.calificacion_instancia?.comentario}>
                        {g.calificacion_instancia?.comentario 
                          ? (g.calificacion_instancia.comentario.length > 50 
                              ? g.calificacion_instancia.comentario.substring(0, 50) + '...' 
                              : g.calificacion_instancia.comentario)
                          : '-'}
                      </td>
                      <td className="tiempo-cell">
                        {tiempoAtencion !== null ? (
                          <span className={`tiempo-badge ${tiempoAtencion <= 15 ? 'rapido' : tiempoAtencion <= 30 ? 'normal' : 'lento'}`}>
                            <Clock size={12} />
                            {tiempoAtencion} días
                          </span>
                        ) : '-'}
                      </td>
                      <td className="fecha-cell">
                        {g.fecha_asignacion ? formatLocalDate(g.fecha_asignacion) : '-'}
                      </td>
                      <td className="fecha-cell">
                        {g.estado === 'Completada / Archivada' && g.updatedAt 
                          ? formatLocalDate(g.updatedAt) 
                          : <span className="estado-pendiente">En proceso</span>}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Resumen */}
      <div className="resumen-panel">
        <div className="resumen-item">
          <span className="resumen-label">Total Gestiones:</span>
          <span className="resumen-valor">{datosFiltrados.length}</span>
        </div>
        <div className="resumen-item">
          <span className="resumen-label">Con Calificación:</span>
          <span className="resumen-valor">
            {datosFiltrados.filter(g => g.calificacion_instancia?.puntuacion).length}
          </span>
        </div>
        <div className="resumen-item">
          <span className="resumen-label">Promedio Calificación:</span>
          <span className="resumen-valor">
            {(() => {
              const conCalif = datosFiltrados.filter(g => g.calificacion_instancia?.puntuacion);
              if (conCalif.length === 0) return '-';
              const promedio = conCalif.reduce((acc, g) => acc + g.calificacion_instancia.puntuacion, 0) / conCalif.length;
              return `${promedio.toFixed(1)} ★`;
            })()}
          </span>
        </div>
        <div className="resumen-item">
          <span className="resumen-label">Tiempo Promedio:</span>
          <span className="resumen-valor">
            {(() => {
              const tiempos = datosFiltrados
                .map(g => calcularTiempoAtencion(g.fecha_asignacion, g.updatedAt))
                .filter(t => t !== null);
              if (tiempos.length === 0) return '-';
              const promedio = tiempos.reduce((a, b) => a + b, 0) / tiempos.length;
              return `${promedio.toFixed(0)} días`;
            })()}
          </span>
        </div>
      </div>
    </div>
  );
};

export default ConsultaCalificaciones;
